var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { useContext, useEffect, useRef, useState, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { t } from 'i18next';
import { DataContext } from '../player/DataContext';
import globalStore from '../store';
import { selectTool, bringManipulativesToFront } from '../helper/helper';
import { playAudio } from '../helper/audioHelper';
import { calculateTokenPosition, findNearestCell, findEmptyCells } from '../helper/tokenDropHelper';
import customEventHandler from '../helper/customEvents';
import { saveState } from '../helper/stateManagerHelper';
import { registerMultiSelectSaveEntries } from '../helper/multiSelectSaveCoordinator';
import { announce } from '../helper/announcer';
import { NF_CELL_SIZE } from '../constants/gridConstants';
import '../styles/widgets/counter.scss';
// Number-Frame token asset keys → existing translation keys that hold a
// human-readable shape name ("Butterfly", "Flower", …). Used only in the
// counter's aria-label so screen readers say "Butterfly counter" instead of
// "shape0 counter". Standalone Counter / Money shapes (`circle`, `penny`, …)
// already carry readable names and are passed through unchanged.
var NF_SHAPE_LABEL_KEYS = {
    shape0: 'text.shape1',
    shape1: 'text.shape2',
    shape2: 'text.shape3',
    shape3: 'text.shape4',
    shape4: 'text.shape5',
    shape5: 'text.shape6',
    shape6: 'text.shape7',
    shape7: 'text.shape8',
};
var Counter = function (_a) {
    var id = _a.id, data = _a.data;
    var shape = data.shape, color = data.color, position = data.center, dimensions = data.dimensions, _b = data.scale, scale = _b === void 0 ? 1 : _b, _c = data.rotation, rotation = _c === void 0 ? 0 : _c, lock = data.lock, frameId = data.frameId;
    var _d = useContext(DataContext), setManipulative = _d.setManipulative, selectedTool = _d.selectedTool, setSelectedTool = _d.setSelectedTool, manipulative = _d.manipulative, panningEnable = _d.panningEnable, setManiplulatorComponentVisible = _d.setManiplulatorComponentVisible, setActiveManipulator = _d.setActiveManipulator, setSelectedManipulativeId = _d.setSelectedManipulativeId, setGlobalContextMenu = _d.setContextMenu;
    var _e = globalStore.useStoreState(function (store) { return store.playerStore; }), canvasHeight = _e.canvasHeight, canvasWidth = _e.canvasWidth;
    var zoomLevel = globalStore.useStoreState(function (store) { return store.playerStore; }).zoomLevel;
    var _f = useState(false), isDragging = _f[0], setIsDragging = _f[1];
    var _g = useState(false), isMultiSelectMoving = _g[0], setIsMultiSelectMoving = _g[1];
    var _h = useState(false), isHovered = _h[0], setIsHovered = _h[1];
    // Boosted while the PARENT frame (not this counter itself) is being dragged. The frame
    // raises its own zIndex to 9999 while dragging (NumberFrames.tsx), but this counter is a
    // separate, independently-positioned manipulative — not a DOM child of the frame — so
    // without a matching boost it renders BEHIND the dragged frame the whole time it moves.
    var _j = useState(false), isFrameDragging = _j[0], setIsFrameDragging = _j[1];
    useEffect(function () {
        if (!frameId)
            return;
        var onFrameDragActive = function (payload) {
            if (payload.frameId !== frameId)
                return;
            setIsFrameDragging(payload.active);
        };
        customEventHandler.addEventListener('numberFrameDragActive', onFrameDragActive);
        return function () {
            return customEventHandler.removeEventListener('numberFrameDragActive', onFrameDragActive);
        };
    }, [frameId]);
    // Local context menu (lock / unlock only) used for *standalone* counters —
    // framed counters now use the shared global ContextMenu instead.
    var _k = useState(null), localContextMenu = _k[0], setLocalContextMenu = _k[1];
    var dragStartRef = useRef(null);
    var counterRef = useRef(null);
    var lastCanvasPosRef = useRef({ x: 0, y: 0 });
    var contextMenuRef = useRef(null);
    var longPressRef = useRef(null);
    // Multi-select move refs
    var multiSelectStartPositions = useRef([]);
    var selectedToolRef = useRef(selectedTool);
    var positionRef = useRef(position);
    // Tracks the last position computed in onMove synchronously (positionRef lags by one render)
    var lastMovePositionRef = useRef(position);
    useEffect(function () {
        selectedToolRef.current = selectedTool;
    }, [selectedTool]);
    useEffect(function () {
        positionRef.current = position;
    }, [position]);
    // Always-current manipulative ref so handleMouseUp frame detection uses latest state
    var manipulativeRef = useRef(manipulative);
    useEffect(function () {
        manipulativeRef.current = manipulative;
    }, [manipulative]);
    var width = dimensions.width;
    var height = dimensions.height;
    // A Number Frame token — framed in a cell OR dropped outside as overflow.
    // Used to gate the new keyboard + global-context-menu behavior. Standalone
    // Counter / Money shapes (circle, penny, etc.) do not start with "shape" and
    // so keep their original role="img" + local lock-only menu behavior.
    var isNfToken = typeof shape === 'string' && shape.startsWith('shape');
    // Given a canvas position, check if it overlaps any frame.
    // Returns only the frameId and nearest cell hint — cell assignment is done inside setManipulative
    // so the functional updater chain (live prev) handles multi-counter collisions correctly.
    var getSnapToFrame = function (canvasX, canvasY, manipulativeArray) {
        var _a, _b;
        for (var i = manipulativeArray.length - 1; i >= 0; i--) {
            var item = manipulativeArray[i];
            if (item.data.type !== 'numberFrames')
                continue;
            var frame = item.data;
            if ((_a = frame.lock) === null || _a === void 0 ? void 0 : _a.editing)
                continue;
            // Skip frames that are also selected (moving with us)
            if (selectedToolRef.current.includes(frame.id))
                continue;
            var frameScale = (_b = frame.scale) !== null && _b !== void 0 ? _b : 1;
            var halfW = ((frame.cols * NF_CELL_SIZE) / 2) * frameScale;
            var halfH = ((frame.rows * NF_CELL_SIZE) / 2) * frameScale;
            var localX = canvasX - frame.center.x;
            var localY = canvasY - frame.center.y;
            if (frame.rotation) {
                var angle = -(frame.rotation * Math.PI) / 180;
                var rx = localX * Math.cos(angle) - localY * Math.sin(angle);
                var ry = localX * Math.sin(angle) + localY * Math.cos(angle);
                localX = rx;
                localY = ry;
            }
            var snapTolerance = (NF_CELL_SIZE / 2) * frameScale;
            if (Math.abs(localX) >= halfW + snapTolerance || Math.abs(localY) >= halfH + snapTolerance)
                continue;
            return { frameId: frame.id, nearestCell: findNearestCell(canvasX, canvasY, frame) };
        }
        return null;
    };
    // Calculate dynamic position for tokens inside frames
    // During drag or when locked, use stored center directly
    var actualPositionRef = useRef(position);
    var actualPosition = useMemo(function () {
        if (isDragging || isMultiSelectMoving || !frameId || lock.positioning) {
            return position;
        }
        var frameItem = manipulative.find(function (item) { return item.data.type === 'numberFrames' && item.data.id === frameId; });
        if (!frameItem) {
            return position;
        }
        var frame = frameItem.data;
        var cellIndex = -1;
        if (frame.tokens) {
            for (var _i = 0, _a = Object.entries(frame.tokens); _i < _a.length; _i++) {
                var _b = _a[_i], cellIndexStr = _b[0], tokenId = _b[1];
                if (tokenId === id) {
                    cellIndex = parseInt(cellIndexStr);
                    break;
                }
            }
        }
        if (cellIndex === -1) {
            console.warn('[Counter] cellIndex not found in frame.tokens — falling back to counter.center', { id: id, frameId: frameId, tokens: frame.tokens });
            return position;
        }
        return calculateTokenPosition(frame, cellIndex);
    }, [frameId, manipulative, position, id, isDragging, isMultiSelectMoving, lock.positioning]);
    // Row / column within the parent frame — null when this counter isn't in a
    // cell (standalone counter, or an overflow NF token that didn't snap). Used
    // to extend the aria-label so screen readers announce "... row R, column C".
    var cellPosition = useMemo(function () {
        if (!frameId)
            return null;
        var frameItem = manipulative.find(function (m) { return m.data.type === 'numberFrames' && m.data.id === frameId; });
        if (!frameItem)
            return null;
        var frame = frameItem.data;
        if (!frame.tokens)
            return null;
        for (var _i = 0, _a = Object.entries(frame.tokens); _i < _a.length; _i++) {
            var _b = _a[_i], cellIndexStr = _b[0], tokenId = _b[1];
            if (tokenId === id) {
                var cellIdx = parseInt(cellIndexStr, 10);
                if (Number.isNaN(cellIdx))
                    return null;
                return {
                    row: Math.floor(cellIdx / frame.cols) + 1,
                    col: (cellIdx % frame.cols) + 1,
                };
            }
        }
        return null;
    }, [frameId, manipulative, id]);
    // Keep ref in sync so handleMouseDown always reads the latest visual position
    actualPositionRef.current = actualPosition;
    var isDraggable = !lock.positioning && !lock.editing;
    // Close local (lock-only) context menu when clicking outside it
    useEffect(function () {
        if (!localContextMenu)
            return;
        var close = function (e) {
            if (contextMenuRef.current && !contextMenuRef.current.contains(e.target)) {
                setLocalContextMenu(null);
            }
        };
        window.addEventListener('mousedown', close, true);
        window.addEventListener('touchstart', close, true);
        return function () {
            window.removeEventListener('mousedown', close, true);
            window.removeEventListener('touchstart', close, true);
        };
    }, [localContextMenu]);
    // Cleanup long-press timer on unmount
    useEffect(function () { return function () {
        if (longPressRef.current)
            clearTimeout(longPressRef.current);
    }; }, []);
    var toggleLock = function (e) {
        e.stopPropagation();
        var newLocked = !lock.positioning;
        // When locking a frame token, snapshot the current calculated position into
        // center so that actualPosition (which falls back to `position` when locked)
        // keeps the token exactly where it visually was.
        var snappedCenter = newLocked ? actualPosition : position;
        setManipulative(function (prev) {
            return prev.map(function (item) {
                return item.data.id === id
                    ? __assign(__assign({}, item), { data: __assign(__assign({}, item.data), { center: snappedCenter, lock: { positioning: newLocked, editing: newLocked } }) }) : item;
            });
        });
        setLocalContextMenu(null);
    };
    // Number-Frame tokens (framed OR overflow / dragged outside) open the shared
    // global ContextMenu (rich action list + keyboard support). Standalone
    // Counter / Money shapes keep the original local lock-only menu so that flow
    // stays byte-identical.
    var openCounterContextMenu = function (x, y) {
        if (isNfToken) {
            if (!selectedToolRef.current.includes(id)) {
                setSelectedTool([id]);
            }
            setGlobalContextMenu({ x: x, y: y, manipulativeId: id });
        }
        else {
            setLocalContextMenu({ x: x, y: y });
        }
    };
    var handleContextMenu = function (e) {
        e.preventDefault();
        e.stopPropagation();
        openCounterContextMenu(e.clientX, e.clientY);
    };
    // Enter / Space / ContextMenu / Shift+F10 on a focused NF token opens the
    // global context menu. Standalone Counter / Money shapes don't register this.
    var handleKeyDown = function (e) {
        var _a;
        if (!isNfToken)
            return;
        // Escape on a cell shape token returns focus to its parent frame widget
        // so the user can re-enter the frame-level keyboard flow (Enter opens the
        // frame's context menu, arrows nudge, etc.).
        if (e.key === 'Escape' && frameId) {
            e.preventDefault();
            e.stopPropagation();
            var frameEl = document.querySelector(".numberFramesWidget[data-manipulative-id=\"".concat(frameId, "\"]"));
            frameEl === null || frameEl === void 0 ? void 0 : frameEl.focus();
            return;
        }
        var opensMenu = e.key === 'Enter' ||
            e.key === ' ' ||
            e.key === 'ContextMenu' ||
            (e.shiftKey && e.key === 'F10');
        if (!opensMenu)
            return;
        e.preventDefault();
        e.stopPropagation();
        var rect = (_a = counterRef.current) === null || _a === void 0 ? void 0 : _a.getBoundingClientRect();
        if (rect)
            openCounterContextMenu(rect.right, rect.top);
    };
    var handleMouseDown = function (e) {
        if (panningEnable) {
            e.stopPropagation();
            return;
        }
        if (!isDraggable || lock.editing) {
            // Allow click-to-select even when locked; drag setup is skipped
            e.stopPropagation();
            return;
        }
        e.preventDefault();
        e.stopPropagation();
        // Select. Bring-to-front is CSS-only while selected/dragging (see the
        // wrapper style below) — the manipulative array is only permanently
        // reordered on drop, in handleMouseUp, so a plain click never touches
        // array order.
        setSelectedTool(selectTool(e, id, selectedToolRef.current, false));
        var clientX = 'clientX' in e ? e.clientX : e.touches[0].clientX;
        var clientY = 'clientY' in e ? e.clientY : e.touches[0].clientY;
        // Capture the current visual position BEFORE isDragging flips (which changes actualPosition back to stored center)
        var visualPos = actualPositionRef.current;
        // Sync stored center to visual position so there's no jump when isDragging=true makes
        // actualPosition fall back to `position` (stored center).
        if (frameId && (visualPos.x !== position.x || visualPos.y !== position.y)) {
            setManipulative(function (prev) {
                return prev.map(function (m) {
                    return m.data.id === id
                        ? __assign(__assign({}, m), { data: __assign(__assign({}, m.data), { center: { x: visualPos.x, y: visualPos.y } }) }) : m;
                });
            });
        }
        setIsDragging(true);
        dragStartRef.current = {
            x: clientX,
            y: clientY,
            startPos: { x: visualPos.x, y: visualPos.y },
            hasMoved: false,
            completeData: JSON.parse(JSON.stringify(data)),
        };
        // Multi-select: capture start positions of all selected NumberFrames and Counters
        if (selectedTool.length > 1 && selectedTool.includes(id)) {
            multiSelectStartPositions.current = manipulative
                .filter(function (item) {
                return item.data.id !== undefined &&
                    selectedTool.includes(item.data.id) &&
                    (item.data.type === 'numberFrames' || item.data.type === 'numberFrameCounter');
            })
                .map(function (item) { return ({
                id: item.data.id,
                center: __assign({}, item.data.center),
                completeData: JSON.parse(JSON.stringify(item.data)),
            }); });
            customEventHandler.dispatchEvent('multiSelectMoveStart', { sourceId: id });
        }
    };
    var handleTouchStart = function (e) {
        var clientX = e.touches[0].clientX;
        var clientY = e.touches[0].clientY;
        // Long-press: show context menu after 600ms without movement
        longPressRef.current = setTimeout(function () {
            longPressRef.current = null;
            setIsDragging(false);
            dragStartRef.current = null;
            openCounterContextMenu(clientX, clientY);
        }, 600);
        handleMouseDown(e);
    };
    useEffect(function () {
        if (!isDragging || !dragStartRef.current)
            return;
        var cancelLongPress = function () {
            if (longPressRef.current) {
                clearTimeout(longPressRef.current);
                longPressRef.current = null;
            }
        };
        var handleMouseMove = function (e) {
            var _a;
            cancelLongPress();
            if (!dragStartRef.current)
                return;
            if (!('touches' in e) && e.buttons === 0) {
                dragStartRef.current = null;
                setIsDragging(false);
                return;
            }
            var clientX = 'clientX' in e ? e.clientX : e.touches[0].clientX;
            var clientY = 'clientY' in e ? e.clientY : e.touches[0].clientY;
            var clientDeltaX = clientX - dragStartRef.current.x;
            var clientDeltaY = clientY - dragStartRef.current.y;
            // Threshold in client pixels (screen pixels), not canvas pixels, so it's zoom-independent
            if (!dragStartRef.current.hasMoved &&
                Math.abs(clientDeltaX) < 5 &&
                Math.abs(clientDeltaY) < 5) {
                return;
            }
            dragStartRef.current.hasMoved = true;
            var deltaX = clientDeltaX / zoomLevel;
            var deltaY = clientDeltaY / zoomLevel;
            var newX = dragStartRef.current.startPos.x + deltaX;
            var newY = dragStartRef.current.startPos.y + deltaY;
            var constrainedX = Math.max(0, Math.min(canvasWidth, newX));
            var constrainedY = Math.max(0, Math.min(canvasHeight, newY));
            lastCanvasPosRef.current = { x: constrainedX, y: constrainedY };
            setManipulative(function (prev) {
                return prev.map(function (item) {
                    if (item.data.id === id) {
                        return __assign(__assign({}, item), { data: __assign(__assign({}, item.data), { center: { x: constrainedX, y: constrainedY } }) });
                    }
                    return item;
                });
            });
            // Multi-select: broadcast delta so other selected items follow
            if (selectedToolRef.current.length > 1 && selectedToolRef.current.includes(id)) {
                var startPos = (_a = dragStartRef.current) === null || _a === void 0 ? void 0 : _a.startPos;
                if (startPos) {
                    customEventHandler.dispatchEvent('multiSelectMove', {
                        deltaX: constrainedX - startPos.x,
                        deltaY: constrainedY - startPos.y,
                        sourceId: id,
                    });
                }
            }
        };
        var handleMouseUp = function () {
            var _a;
            var _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p, _q, _r, _s, _t, _u, _v, _w, _x, _y, _z, _0, _1, _2, _3, _4, _5, _6, _7, _8, _9, _10, _11;
            cancelLongPress();
            var hasMoved = (_c = (_b = dragStartRef.current) === null || _b === void 0 ? void 0 : _b.hasMoved) !== null && _c !== void 0 ? _c : false;
            var isMultiSelectSource = selectedToolRef.current.length > 1 && selectedToolRef.current.includes(id);
            setIsDragging(false);
            if (!hasMoved) {
                dragStartRef.current = null;
                return;
            }
            // Bring the dragged counter (and, if selected, its frame + all the
            // frame's attached tokens; standalone counters last) to front by moving
            // them to the end of the manipulative array. Only committed here, on
            // drop after a real drag — a plain click never reorders the array (CSS
            // zIndex handles the click/drag-in-progress visual boost instead).
            {
                var selectedIds = isMultiSelectSource
                    ? selectedToolRef.current.filter(function (t) { return t.startsWith('numberFrameCounter_') || t.startsWith('numberFrame_'); })
                    : [id];
                var framesToUpdate = new Set();
                var standaloneCounterIds = [];
                var _loop_1 = function (selId) {
                    if (selId.startsWith('numberFrame_')) {
                        framesToUpdate.add(selId);
                    }
                    else {
                        var ctr = (_d = manipulativeRef.current.find(function (m) { return m.data.id === selId; })) === null || _d === void 0 ? void 0 : _d.data;
                        if (ctr === null || ctr === void 0 ? void 0 : ctr.frameId)
                            framesToUpdate.add(ctr.frameId);
                        else
                            standaloneCounterIds.push(selId);
                    }
                };
                for (var _i = 0, selectedIds_1 = selectedIds; _i < selectedIds_1.length; _i++) {
                    var selId = selectedIds_1[_i];
                    _loop_1(selId);
                }
                var idsToPromote_1 = [];
                var _loop_2 = function (fid) {
                    var frameItem = manipulativeRef.current.find(function (m) { return m.data.id === fid && m.data.type === 'numberFrames'; });
                    if (!frameItem)
                        return "continue";
                    idsToPromote_1.push(fid);
                    manipulativeRef.current
                        .filter(function (m) { return m.data.type === 'numberFrameCounter' && m.data.frameId === fid; })
                        .forEach(function (m) { var _a; return idsToPromote_1.push((_a = m.data.id) !== null && _a !== void 0 ? _a : ''); });
                };
                for (var _12 = 0, _13 = Array.from(framesToUpdate); _12 < _13.length; _12++) {
                    var fid = _13[_12];
                    _loop_2(fid);
                }
                idsToPromote_1.push.apply(idsToPromote_1, standaloneCounterIds);
                var beforeIds_1 = manipulativeRef.current.map(function (m) { var _a; return (_a = m.data.id) !== null && _a !== void 0 ? _a : ''; });
                var alreadyAtFront = idsToPromote_1.every(function (promoteId, i) { return beforeIds_1[beforeIds_1.length - idsToPromote_1.length + i] === promoteId; });
                if (idsToPromote_1.length > 0 && !alreadyAtFront) {
                    var reordered_1 = bringManipulativesToFront(manipulativeRef.current, idsToPromote_1);
                    saveState({
                        type: id,
                        id: id,
                        propertyName: 'reorder',
                        old: JSON.stringify(beforeIds_1),
                        new: JSON.stringify(reordered_1.map(function (m) { var _a; return (_a = m.data.id) !== null && _a !== void 0 ? _a : ''; })),
                    });
                    setManipulative(function () { return reordered_1; });
                }
            }
            // Multi-select group move: snap ALL selected counters to frame if drop lands on one, batch saveState, signal end
            if (isMultiSelectSource) {
                var canvasX_1 = lastCanvasPosRef.current.x;
                var canvasY_1 = lastCanvasPosRef.current.y;
                // Find a frame at the source counter's drop point — skip frames that are also selected
                var findFrameAtPoint = function (px, py) {
                    var _a;
                    for (var i = manipulativeRef.current.length - 1; i >= 0; i--) {
                        var item = manipulativeRef.current[i];
                        if (item.data.type !== 'numberFrames')
                            continue;
                        var frame = item.data;
                        if (selectedToolRef.current.includes(frame.id))
                            continue;
                        var frameScale = (_a = frame.scale) !== null && _a !== void 0 ? _a : 1;
                        var halfW = ((frame.cols * NF_CELL_SIZE) / 2) * frameScale;
                        var halfH = ((frame.rows * NF_CELL_SIZE) / 2) * frameScale;
                        var localX = px - frame.center.x;
                        var localY = py - frame.center.y;
                        if (frame.rotation) {
                            var angle = -(frame.rotation * Math.PI) / 180;
                            var rx = localX * Math.cos(angle) - localY * Math.sin(angle);
                            var ry = localX * Math.sin(angle) + localY * Math.cos(angle);
                            localX = rx;
                            localY = ry;
                        }
                        var snapTolerance = (NF_CELL_SIZE / 2) * frameScale;
                        if (Math.abs(localX) < halfW + snapTolerance &&
                            Math.abs(localY) < halfH + snapTolerance) {
                            return frame;
                        }
                    }
                    return null;
                };
                // First try the source counter's drop point; if it missed, check each other selected counter's live position
                var targetFrame_1 = findFrameAtPoint(canvasX_1, canvasY_1);
                if (!targetFrame_1) {
                    var _loop_3 = function (sp) {
                        var liveCtr = (_e = manipulativeRef.current.find(function (m) { return m.data.id === sp.id; })) === null || _e === void 0 ? void 0 : _e.data;
                        var livePos = (_f = liveCtr === null || liveCtr === void 0 ? void 0 : liveCtr.center) !== null && _f !== void 0 ? _f : sp.center;
                        targetFrame_1 = findFrameAtPoint(livePos.x, livePos.y);
                        if (targetFrame_1)
                            return "break";
                    };
                    for (var _14 = 0, _15 = multiSelectStartPositions.current.filter(function (p) { return p.id.startsWith('numberFrameCounter_') && p.id !== id; }); _14 < _15.length; _14++) {
                        var sp = _15[_14];
                        var state_1 = _loop_3(sp);
                        if (state_1 === "break")
                            break;
                    }
                }
                var resolvedFrame_1 = targetFrame_1 && !targetFrame_1.lock.editing ? targetFrame_1 : null;
                var selectedCounterStarts_3 = multiSelectStartPositions.current.filter(function (p) {
                    return p.id.startsWith('numberFrameCounter_');
                });
                // Pre-compute cell assignments for ALL selected counters so setManipulative and saveState agree
                var assignments_1 = new Map();
                if (resolvedFrame_1) {
                    var liveFrameData_1 = (_g = manipulativeRef.current.find(function (m) { return m.data.id === resolvedFrame_1.id; })) === null || _g === void 0 ? void 0 : _g.data;
                    var frameForAssign = liveFrameData_1 !== null && liveFrameData_1 !== void 0 ? liveFrameData_1 : resolvedFrame_1;
                    // Cells already occupied by non-selected counters
                    var usedCells_1 = new Set();
                    var _loop_4 = function (cell, tokenId) {
                        if (!selectedCounterStarts_3.some(function (p) { return p.id === tokenId; })) {
                            usedCells_1.add(Number(cell));
                        }
                    };
                    for (var _16 = 0, _17 = Object.entries(frameForAssign.tokens || {}); _16 < _17.length; _16++) {
                        var _18 = _17[_16], cell = _18[0], tokenId = _18[1];
                        _loop_4(cell, tokenId);
                    }
                    var totalCells = frameForAssign.rows * frameForAssign.cols;
                    var frameScale = (_h = frameForAssign.scale) !== null && _h !== void 0 ? _h : 1;
                    var halfW = ((frameForAssign.cols * NF_CELL_SIZE) / 2) * frameScale;
                    var halfH = ((frameForAssign.rows * NF_CELL_SIZE) / 2) * frameScale;
                    var snapTolerance = (NF_CELL_SIZE / 2) * frameScale;
                    var _loop_5 = function (sp) {
                        // Only snap this counter if its live position is within the frame bounds
                        var liveCtr = (_j = manipulativeRef.current.find(function (m) { return m.data.id === sp.id; })) === null || _j === void 0 ? void 0 : _j.data;
                        var liveCenter = (_k = liveCtr === null || liveCtr === void 0 ? void 0 : liveCtr.center) !== null && _k !== void 0 ? _k : sp.center;
                        var localX = liveCenter.x - frameForAssign.center.x;
                        var localY = liveCenter.y - frameForAssign.center.y;
                        if (frameForAssign.rotation) {
                            var angle = -(frameForAssign.rotation * Math.PI) / 180;
                            var rx = localX * Math.cos(angle) - localY * Math.sin(angle);
                            var ry = localX * Math.sin(angle) + localY * Math.cos(angle);
                            localX = rx;
                            localY = ry;
                        }
                        if (Math.abs(localX) >= halfW + snapTolerance ||
                            Math.abs(localY) >= halfH + snapTolerance)
                            return "continue";
                        var allCells = Array.from({ length: totalCells }, function (_, i) { return i; });
                        var emptyCells = allCells.filter(function (c) { return !usedCells_1.has(c); });
                        if (emptyCells.length === 0)
                            return "break";
                        var nearestCell = findNearestCell(liveCenter.x, liveCenter.y, frameForAssign);
                        var targetCell = emptyCells.includes(nearestCell) ? nearestCell : emptyCells[0];
                        assignments_1.set(sp.id, {
                            cell: targetCell,
                            pos: calculateTokenPosition(frameForAssign, targetCell),
                            scale: frameScale,
                        });
                        usedCells_1.add(targetCell);
                    };
                    for (var _19 = 0, selectedCounterStarts_1 = selectedCounterStarts_3; _19 < selectedCounterStarts_1.length; _19++) {
                        var sp = selectedCounterStarts_1[_19];
                        var state_2 = _loop_5(sp);
                        if (state_2 === "break")
                            break;
                    }
                }
                // Capture old frame tokens before mutation
                var oldFrameTokens_1 = resolvedFrame_1
                    ? __assign({}, ((_o = (_m = (_l = manipulativeRef.current.find(function (m) { return m.data.id === resolvedFrame_1.id; })) === null || _l === void 0 ? void 0 : _l.data) === null || _m === void 0 ? void 0 : _m.tokens) !== null && _o !== void 0 ? _o : {})) : null;
                setManipulative(function (prev) {
                    var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k;
                    var updated = prev;
                    var _loop_8 = function (sp) {
                        var existingFrameId = (_b = (_a = prev.find(function (m) { return m.data.id === sp.id; })) === null || _a === void 0 ? void 0 : _a.data) === null || _b === void 0 ? void 0 : _b.frameId;
                        if (existingFrameId) {
                            updated = updated.map(function (m) {
                                if (m.data.id === existingFrameId && m.data.type === 'numberFrames') {
                                    var f = m.data;
                                    var updatedTokens = Object.fromEntries(Object.entries(f.tokens || {}).filter(function (_a) {
                                        var v = _a[1];
                                        return !selectedCounterStarts_3.some(function (p) { return p.id === v; });
                                    }));
                                    return __assign(__assign({}, m), { data: __assign(__assign({}, f), { tokens: updatedTokens }) });
                                }
                                return m;
                            });
                        }
                    };
                    // Remove all selected counters from their existing frame assignments
                    for (var _i = 0, selectedCounterStarts_4 = selectedCounterStarts_3; _i < selectedCounterStarts_4.length; _i++) {
                        var sp = selectedCounterStarts_4[_i];
                        _loop_8(sp);
                    }
                    if (!resolvedFrame_1 || assignments_1.size === 0) {
                        // No frame target — drop counters at their current live positions
                        var fromFrame = selectedCounterStarts_3.some(function (p) { var _a; return (_a = p.completeData) === null || _a === void 0 ? void 0 : _a.frameId; });
                        if (fromFrame) {
                            var count = selectedCounterStarts_3.length;
                            playAudio(count >= 9 ? 'canvasDrop10' : count >= 5 ? 'canvasDrop5' : 'canvasDrop1', {
                                volume: 0.5,
                            });
                        }
                        return updated.map(function (m) {
                            var _a, _b;
                            if (!selectedCounterStarts_3.some(function (p) { return p.id === m.data.id; }))
                                return m;
                            var liveCtr = (_a = manipulativeRef.current.find(function (li) { return li.data.id === m.data.id; })) === null || _a === void 0 ? void 0 : _a.data;
                            return __assign(__assign({}, m), { data: __assign(__assign({}, m.data), { center: (_b = liveCtr === null || liveCtr === void 0 ? void 0 : liveCtr.center) !== null && _b !== void 0 ? _b : m.data.center, frameId: undefined, cellIndex: undefined, rotation: 0 }) });
                        });
                    }
                    // Apply pre-computed assignments to all selected counters
                    var newFrameTokens = __assign({}, ((_e = (_d = (_c = updated.find(function (m) { return m.data.id === resolvedFrame_1.id; })) === null || _c === void 0 ? void 0 : _c.data) === null || _d === void 0 ? void 0 : _d.tokens) !== null && _e !== void 0 ? _e : {}));
                    var _loop_9 = function (cid, assignment) {
                        newFrameTokens[assignment.cell] = cid;
                        var ctrData = (_f = prev.find(function (m) { return m.data.id === cid; })) === null || _f === void 0 ? void 0 : _f.data;
                        var defaultW = (_h = (_g = ctrData === null || ctrData === void 0 ? void 0 : ctrData.defaultDimensions) === null || _g === void 0 ? void 0 : _g.width) !== null && _h !== void 0 ? _h : 50;
                        var defaultH = (_k = (_j = ctrData === null || ctrData === void 0 ? void 0 : ctrData.defaultDimensions) === null || _j === void 0 ? void 0 : _j.height) !== null && _k !== void 0 ? _k : 50;
                        updated = updated.map(function (m) {
                            var _a;
                            return m.data.id === cid
                                ? __assign(__assign({}, m), { data: __assign(__assign({}, m.data), { center: assignment.pos, frameId: resolvedFrame_1.id, cellIndex: assignment.cell, scale: assignment.scale, rotation: (_a = resolvedFrame_1.rotation) !== null && _a !== void 0 ? _a : 0, dimensions: {
                                            width: defaultW * assignment.scale,
                                            height: defaultH * assignment.scale,
                                        } }) }) : m;
                        });
                    };
                    for (var _l = 0, _m = Array.from(assignments_1); _l < _m.length; _l++) {
                        var _o = _m[_l], cid = _o[0], assignment = _o[1];
                        _loop_9(cid, assignment);
                    }
                    updated = updated.map(function (m) {
                        return m.data.id === resolvedFrame_1.id
                            ? __assign(__assign({}, m), { data: __assign(__assign({}, m.data), { tokens: newFrameTokens }) }) : m;
                    });
                    // Detach selected counters that landed OUTSIDE the frame bounds — they
                    // were already removed from frame.tokens above, but their `frameId`
                    // field still pointed at the frame, so a later frame nudge would
                    // sweep them up by frameId. Clear it so they're truly free counters.
                    var assignedIds = new Set(assignments_1.keys());
                    var detachedIds = [];
                    var _loop_10 = function (sp) {
                        if (assignedIds.has(sp.id))
                            return "continue";
                        detachedIds.push(sp.id);
                        updated = updated.map(function (m) {
                            var _a, _b;
                            if (m.data.id !== sp.id)
                                return m;
                            var liveCtr = (_a = manipulativeRef.current.find(function (li) { return li.data.id === sp.id; })) === null || _a === void 0 ? void 0 : _a.data;
                            return __assign(__assign({}, m), { data: __assign(__assign({}, m.data), { center: (_b = liveCtr === null || liveCtr === void 0 ? void 0 : liveCtr.center) !== null && _b !== void 0 ? _b : m.data.center, frameId: undefined, cellIndex: undefined, rotation: 0 }) });
                        });
                    };
                    for (var _p = 0, selectedCounterStarts_5 = selectedCounterStarts_3; _p < selectedCounterStarts_5.length; _p++) {
                        var sp = selectedCounterStarts_5[_p];
                        _loop_10(sp);
                    }
                    if (assignments_1.size > 0) {
                        var placed = assignments_1.size;
                        playAudio(placed >= 9 ? 'nfDrop10' : placed >= 5 ? 'nfDrop5' : 'nfDrop1', {
                            volume: 0.5,
                        });
                    }
                    if (detachedIds.length > 0) {
                        var out = detachedIds.length;
                        playAudio(out >= 9 ? 'canvasDrop10' : out >= 5 ? 'canvasDrop5' : 'canvasDrop1', {
                            volume: 0.5,
                        });
                    }
                    return updated;
                });
                // Build saveState batch: completeData per counter (restores all fields incl. scale/dimensions)
                var batchSave = [];
                var _loop_6 = function (sp) {
                    var assignment = assignments_1.get(sp.id);
                    var liveCtr = (_p = manipulativeRef.current.find(function (m) { return m.data.id === sp.id; })) === null || _p === void 0 ? void 0 : _p.data;
                    var newData = void 0;
                    if (assignment && resolvedFrame_1) {
                        var defaultW = (_s = (_r = (_q = sp.completeData) === null || _q === void 0 ? void 0 : _q.defaultDimensions) === null || _r === void 0 ? void 0 : _r.width) !== null && _s !== void 0 ? _s : 50;
                        var defaultH = (_v = (_u = (_t = sp.completeData) === null || _t === void 0 ? void 0 : _t.defaultDimensions) === null || _u === void 0 ? void 0 : _u.height) !== null && _v !== void 0 ? _v : 50;
                        newData = __assign(__assign({}, (liveCtr !== null && liveCtr !== void 0 ? liveCtr : sp.completeData)), { center: assignment.pos, frameId: resolvedFrame_1.id, cellIndex: assignment.cell, scale: assignment.scale, rotation: (_w = resolvedFrame_1.rotation) !== null && _w !== void 0 ? _w : 0, dimensions: {
                                width: defaultW * assignment.scale,
                                height: defaultH * assignment.scale,
                            } });
                    }
                    else {
                        newData = __assign(__assign({}, (liveCtr !== null && liveCtr !== void 0 ? liveCtr : sp.completeData)), { frameId: undefined, cellIndex: undefined, rotation: 0 });
                    }
                    batchSave.push({
                        type: sp.id,
                        id: sp.id,
                        propertyName: 'completeData',
                        old: JSON.stringify(sp.completeData),
                        new: JSON.stringify(newData),
                    });
                };
                for (var _20 = 0, selectedCounterStarts_2 = selectedCounterStarts_3; _20 < selectedCounterStarts_2.length; _20++) {
                    var sp = selectedCounterStarts_2[_20];
                    _loop_6(sp);
                }
                // Non-counter selected items (e.g. number frames being dragged together)
                var selfDeltaX = canvasX_1 - ((_y = (_x = multiSelectStartPositions.current.find(function (p) { return p.id === id; })) === null || _x === void 0 ? void 0 : _x.center.x) !== null && _y !== void 0 ? _y : canvasX_1);
                var selfDeltaY = canvasY_1 - ((_0 = (_z = multiSelectStartPositions.current.find(function (p) { return p.id === id; })) === null || _z === void 0 ? void 0 : _z.center.y) !== null && _0 !== void 0 ? _0 : canvasY_1);
                for (var _21 = 0, _22 = multiSelectStartPositions.current.filter(function (p) { return !p.id.startsWith('numberFrameCounter_'); }); _21 < _22.length; _21++) {
                    var sp = _22[_21];
                    batchSave.push({
                        type: sp.id,
                        id: sp.id,
                        propertyName: 'center',
                        old: JSON.stringify(sp.center),
                        new: JSON.stringify({ x: sp.center.x + selfDeltaX, y: sp.center.y + selfDeltaY }),
                    });
                }
                // Frame tokens change
                if (resolvedFrame_1 && oldFrameTokens_1 !== null && assignments_1.size > 0) {
                    var newFrameTokens = __assign({}, oldFrameTokens_1);
                    var _loop_7 = function (cell, tokenId) {
                        if (selectedCounterStarts_3.some(function (p) { return p.id === tokenId; }))
                            delete newFrameTokens[Number(cell)];
                    };
                    // Remove old assignments for selected counters
                    for (var _23 = 0, _24 = Object.entries(newFrameTokens); _23 < _24.length; _23++) {
                        var _25 = _24[_23], cell = _25[0], tokenId = _25[1];
                        _loop_7(cell, tokenId);
                    }
                    for (var _26 = 0, _27 = Array.from(assignments_1); _26 < _27.length; _26++) {
                        var _28 = _27[_26], cid = _28[0], assignment = _28[1];
                        newFrameTokens[assignment.cell] = cid;
                    }
                    batchSave.push({
                        type: resolvedFrame_1.id,
                        id: resolvedFrame_1.id,
                        propertyName: 'tokens',
                        old: JSON.stringify(oldFrameTokens_1),
                        new: JSON.stringify(newFrameTokens),
                    });
                }
                if (!registerMultiSelectSaveEntries(id, batchSave)) {
                    saveState(batchSave);
                }
                customEventHandler.dispatchEvent('multiSelectMoveEnd', { sourceId: id, handled: true });
                dragStartRef.current = null;
                return;
            }
            // Use the last tracked canvas position from drag
            var canvasX = lastCanvasPosRef.current.x;
            var canvasY = lastCanvasPosRef.current.y;
            // Find frame at drop position using canvas coordinates directly
            // Use ref to always get the latest manipulative state (not stale closure value)
            var targetFrame = null;
            for (var i = manipulativeRef.current.length - 1; i >= 0; i--) {
                var item = manipulativeRef.current[i];
                if (item.data.type === 'numberFrames') {
                    var frame = item.data;
                    var frameScale = (_1 = frame.scale) !== null && _1 !== void 0 ? _1 : 1;
                    var halfW = ((frame.cols * NF_CELL_SIZE) / 2) * frameScale;
                    var halfH = ((frame.rows * NF_CELL_SIZE) / 2) * frameScale;
                    // Un-rotate the test point around frame center for rotated frames
                    var localX = canvasX - frame.center.x;
                    var localY = canvasY - frame.center.y;
                    if (frame.rotation) {
                        var angle = -(frame.rotation * Math.PI) / 180;
                        var rx = localX * Math.cos(angle) - localY * Math.sin(angle);
                        var ry = localX * Math.sin(angle) + localY * Math.cos(angle);
                        localX = rx;
                        localY = ry;
                    }
                    var snapTolerance = (NF_CELL_SIZE / 2) * frameScale;
                    if (Math.abs(localX) < halfW + snapTolerance &&
                        Math.abs(localY) < halfH + snapTolerance) {
                        targetFrame = frame;
                        break;
                    }
                }
            }
            // Pre-compute snap outcome using live frame data so setManipulative and saveState agree
            var resolvedFrame = targetFrame && !targetFrame.lock.editing ? targetFrame : null;
            var liveFrameData = resolvedFrame
                ? (_2 = manipulativeRef.current.find(function (m) { return m.data.id === resolvedFrame.id; })) === null || _2 === void 0 ? void 0 : _2.data
                : undefined;
            var liveFrame = (_3 = liveFrameData !== null && liveFrameData !== void 0 ? liveFrameData : resolvedFrame) !== null && _3 !== void 0 ? _3 : null;
            // Old counter data captured at drag start — guaranteed pre-drag state
            var oldCounterData = (_5 = (_4 = dragStartRef.current) === null || _4 === void 0 ? void 0 : _4.completeData) !== null && _5 !== void 0 ? _5 : JSON.parse(JSON.stringify(data));
            // Old frame tokens for saveState
            var oldFrameTokens = liveFrame ? __assign({}, (liveFrame.tokens || {})) : null;
            // Compute snap: cell, snapped position, new tokens
            var snapResult = null;
            if (liveFrame) {
                var isSameFrame = resolvedFrame.id === frameId;
                var frameWithoutSelf = isSameFrame
                    ? __assign(__assign({}, liveFrame), { tokens: Object.fromEntries(Object.entries(liveFrame.tokens || {}).filter(function (_a) {
                            var v = _a[1];
                            return v !== id;
                        })) }) : liveFrame;
                var emptyCells = findEmptyCells(frameWithoutSelf);
                var nearestCell = findNearestCell(canvasX, canvasY, liveFrame);
                var targetCell = emptyCells.includes(nearestCell) ? nearestCell : (_6 = emptyCells[0]) !== null && _6 !== void 0 ? _6 : -1;
                if (targetCell !== -1) {
                    var baseTokens = isSameFrame
                        ? Object.fromEntries(Object.entries(liveFrame.tokens || {}).filter(function (_a) {
                            var v = _a[1];
                            return v !== id;
                        }))
                        : __assign({}, (liveFrame.tokens || {}));
                    snapResult = {
                        cell: targetCell,
                        pos: calculateTokenPosition(liveFrame, targetCell),
                        scale: (_7 = liveFrame.scale) !== null && _7 !== void 0 ? _7 : 1,
                        newTokens: __assign(__assign({}, baseTokens), (_a = {}, _a[targetCell] = id, _a)),
                    };
                }
            }
            if (resolvedFrame) {
                playAudio('nfDrop1', { volume: 0.5 });
            }
            else if (frameId || targetFrame) {
                playAudio('canvasDrop1', { volume: 0.5 });
            }
            setManipulative(function (prev) {
                var _a, _b, _c, _d, _e;
                var updated = prev;
                // Remove counter from old frame tokens if it was in a frame
                if (frameId) {
                    updated = updated.map(function (m) {
                        if (m.data.id === frameId && m.data.type === 'numberFrames') {
                            var f = m.data;
                            var updatedTokens = __assign({}, (f.tokens || {}));
                            for (var _i = 0, _a = Object.entries(updatedTokens); _i < _a.length; _i++) {
                                var _b = _a[_i], cellIdx = _b[0], tokenId = _b[1];
                                if (tokenId === id) {
                                    delete updatedTokens[Number(cellIdx)];
                                    break;
                                }
                            }
                            return __assign(__assign({}, m), { data: __assign(__assign({}, f), { tokens: updatedTokens }) });
                        }
                        return m;
                    });
                }
                if (resolvedFrame && snapResult) {
                    // Snap to frame cell
                    var defaultW_1 = (_b = (_a = data.defaultDimensions) === null || _a === void 0 ? void 0 : _a.width) !== null && _b !== void 0 ? _b : 50;
                    var defaultH_1 = (_d = (_c = data.defaultDimensions) === null || _c === void 0 ? void 0 : _c.height) !== null && _d !== void 0 ? _d : 50;
                    updated = updated.map(function (m) {
                        return m.data.id === resolvedFrame.id
                            ? __assign(__assign({}, m), { data: __assign(__assign({}, m.data), { tokens: snapResult.newTokens }) }) : m;
                    });
                    return updated.map(function (m) {
                        var _a;
                        return m.data.id === id
                            ? __assign(__assign({}, m), { data: __assign(__assign({}, m.data), { center: snapResult.pos, frameId: resolvedFrame.id, cellIndex: snapResult.cell, scale: snapResult.scale, rotation: (_a = resolvedFrame.rotation) !== null && _a !== void 0 ? _a : 0, dimensions: {
                                        width: defaultW_1 * snapResult.scale,
                                        height: defaultH_1 * snapResult.scale,
                                    } }) }) : m;
                    });
                }
                if (resolvedFrame && !snapResult) {
                    // Frame full — overflow: drop next to frame
                    var frameScale = (_e = liveFrame.scale) !== null && _e !== void 0 ? _e : 1;
                    var frameRight_1 = liveFrame.center.x + ((liveFrame.cols * NF_CELL_SIZE) / 2) * frameScale;
                    return updated.map(function (m) {
                        return m.data.id === id
                            ? __assign(__assign({}, m), { data: __assign(__assign({}, m.data), { center: { x: frameRight_1 + NF_CELL_SIZE, y: liveFrame.center.y }, frameId: undefined, cellIndex: undefined, rotation: 0 }) }) : m;
                    });
                }
                // Free drop on canvas
                return updated.map(function (m) {
                    return m.data.id === id
                        ? __assign(__assign({}, m), { data: __assign(__assign({}, m.data), { center: { x: canvasX, y: canvasY }, frameId: undefined, cellIndex: undefined, rotation: 0 }) }) : m;
                });
            });
            // Build saveState with full completeData + frame tokens change
            var newCounterData = snapResult && resolvedFrame
                ? __assign(__assign({}, oldCounterData), { center: snapResult.pos, frameId: resolvedFrame.id, cellIndex: snapResult.cell, scale: snapResult.scale, dimensions: {
                        width: ((_9 = (_8 = data.defaultDimensions) === null || _8 === void 0 ? void 0 : _8.width) !== null && _9 !== void 0 ? _9 : 50) * snapResult.scale,
                        height: ((_11 = (_10 = data.defaultDimensions) === null || _10 === void 0 ? void 0 : _10.height) !== null && _11 !== void 0 ? _11 : 50) * snapResult.scale,
                    } }) : __assign(__assign({}, oldCounterData), { center: { x: canvasX, y: canvasY }, frameId: undefined, cellIndex: undefined, rotation: 0 });
            var singleBatch = [
                {
                    type: id,
                    id: id,
                    propertyName: 'completeData',
                    old: JSON.stringify(oldCounterData),
                    new: JSON.stringify(newCounterData),
                },
            ];
            if (resolvedFrame && oldFrameTokens !== null && snapResult) {
                singleBatch.push({
                    type: resolvedFrame.id,
                    id: resolvedFrame.id,
                    propertyName: 'tokens',
                    old: JSON.stringify(oldFrameTokens),
                    new: JSON.stringify(snapResult.newTokens),
                });
            }
            saveState(singleBatch);
            dragStartRef.current = null;
        };
        window.addEventListener('mousemove', handleMouseMove);
        window.addEventListener('mouseup', handleMouseUp);
        window.addEventListener('touchmove', handleMouseMove);
        window.addEventListener('touchend', handleMouseUp);
        return function () {
            window.removeEventListener('mousemove', handleMouseMove);
            window.removeEventListener('mouseup', handleMouseUp);
            window.removeEventListener('touchmove', handleMouseMove);
            window.removeEventListener('touchend', handleMouseUp);
        };
    }, [
        isDragging,
        id,
        setManipulative,
        canvasWidth,
        canvasHeight,
        frameId,
        manipulative,
        zoomLevel,
    ]);
    // Multi-select listener: respond when another selected item is dragged
    useEffect(function () {
        if (!selectedTool.includes(id) || selectedTool.length <= 1)
            return;
        var myStart = { x: positionRef.current.x, y: positionRef.current.y };
        var onStart = function (_a) {
            var sourceId = _a.sourceId;
            if (sourceId === id)
                return;
            setIsMultiSelectMoving(false);
            // Use ref so we always get the position at the moment drag starts, not stale closure value
            myStart = { x: positionRef.current.x, y: positionRef.current.y };
            lastMovePositionRef.current = positionRef.current;
        };
        var onMove = function (_a) {
            var deltaX = _a.deltaX, deltaY = _a.deltaY, sourceId = _a.sourceId;
            if (sourceId === id || lock.positioning)
                return;
            // Skip if this counter is attached to a frame that is also selected — the frame handles it
            if (frameId && selectedToolRef.current.includes(frameId))
                return;
            setIsMultiSelectMoving(true);
            var newX = Math.max(0, Math.min(canvasWidth, myStart.x + deltaX));
            var newY = Math.max(0, Math.min(canvasHeight, myStart.y + deltaY));
            lastMovePositionRef.current = { x: newX, y: newY };
            setManipulative(function (prev) {
                return prev.map(function (item) {
                    return item.data.id === id
                        ? __assign(__assign({}, item), { data: __assign(__assign({}, item.data), { center: { x: newX, y: newY } }) }) : item;
                });
            });
        };
        // On drag end, finalize snap: assign frameId and update frame tokens for this counter
        var onEnd = function (_a) {
            var sourceId = _a.sourceId, handled = _a.handled;
            if (sourceId === id || lock.positioning)
                return;
            // Source counter already batched all snaps in handleMouseUp — skip individual snap
            if (handled) {
                setIsMultiSelectMoving(false);
                return;
            }
            if (frameId && selectedToolRef.current.includes(frameId))
                return;
            setIsMultiSelectMoving(false);
            // Use lastMovePositionRef — it's updated synchronously in onMove so it's always current,
            // unlike positionRef which lags by one render cycle
            var currentPos = lastMovePositionRef.current;
            var snapResult = getSnapToFrame(currentPos.x, currentPos.y, manipulativeRef.current);
            if (!snapResult) {
                // Dropped outside any frame — clear frameId so counter stays at dragged position
                if (frameId) {
                    setManipulative(function (prev) {
                        return prev.map(function (m) {
                            if (m.data.id === frameId && m.data.type === 'numberFrames') {
                                var f = m.data;
                                var tokens = __assign({}, (f.tokens || {}));
                                for (var _i = 0, _a = Object.entries(tokens); _i < _a.length; _i++) {
                                    var _b = _a[_i], k = _b[0], v = _b[1];
                                    if (v === id) {
                                        delete tokens[Number(k)];
                                        break;
                                    }
                                }
                                return __assign(__assign({}, m), { data: __assign(__assign({}, f), { tokens: tokens }) });
                            }
                            if (m.data.id === id) {
                                return __assign(__assign({}, m), { data: __assign(__assign({}, m.data), { frameId: undefined, cellIndex: undefined, rotation: 0 }) });
                            }
                            return m;
                        });
                    });
                }
                return;
            }
            var snapFrameId = snapResult.frameId, snapNearestCell = snapResult.nearestCell;
            setManipulative(function (prev) {
                var _a, _b, _c, _d, _e, _f;
                // Remove from old frame if needed
                var updated = prev;
                if (frameId) {
                    updated = updated.map(function (m) {
                        if (m.data.id === frameId && m.data.type === 'numberFrames') {
                            var f = m.data;
                            var tokens = __assign({}, (f.tokens || {}));
                            for (var _i = 0, _a = Object.entries(tokens); _i < _a.length; _i++) {
                                var _b = _a[_i], k = _b[0], v = _b[1];
                                if (v === id) {
                                    delete tokens[Number(k)];
                                    break;
                                }
                            }
                            return __assign(__assign({}, m), { data: __assign(__assign({}, f), { tokens: tokens }) });
                        }
                        return m;
                    });
                }
                // Read live frame from prev — this is the key: each updater in the chain sees
                // the placements made by previous counters, preventing cell collisions
                var liveFrameItem = updated.find(function (m) { return m.data.id === snapFrameId && m.data.type === 'numberFrames'; });
                var liveFrame = liveFrameItem ? liveFrameItem.data : null;
                if (!liveFrame)
                    return updated;
                var emptyCells = findEmptyCells(__assign(__assign({}, liveFrame), { tokens: Object.fromEntries(Object.entries(liveFrame.tokens || {}).filter(function (_a) {
                        var v = _a[1];
                        return v !== id;
                    })) }));
                var targetCell = emptyCells.includes(snapNearestCell)
                    ? snapNearestCell
                    : (_a = emptyCells[0]) !== null && _a !== void 0 ? _a : -1;
                if (targetCell === -1)
                    return updated;
                var finalPos = calculateTokenPosition(liveFrame, targetCell);
                var targetScale = (_b = liveFrame.scale) !== null && _b !== void 0 ? _b : 1;
                var defaultW = (_d = (_c = data.defaultDimensions) === null || _c === void 0 ? void 0 : _c.width) !== null && _d !== void 0 ? _d : 50;
                var defaultH = (_f = (_e = data.defaultDimensions) === null || _e === void 0 ? void 0 : _e.height) !== null && _f !== void 0 ? _f : 50;
                updated = updated.map(function (m) {
                    var _a;
                    if (m.data.id === snapFrameId && m.data.type === 'numberFrames') {
                        var f = m.data;
                        return __assign(__assign({}, m), { data: __assign(__assign({}, f), { tokens: __assign(__assign({}, (f.tokens || {})), (_a = {}, _a[targetCell] = id, _a)) }) });
                    }
                    return m;
                });
                return updated.map(function (m) {
                    var _a;
                    return m.data.id === id
                        ? __assign(__assign({}, m), { data: __assign(__assign({}, m.data), { center: finalPos, frameId: snapFrameId, cellIndex: targetCell, scale: targetScale, rotation: (_a = liveFrame.rotation) !== null && _a !== void 0 ? _a : 0, dimensions: { width: defaultW * targetScale, height: defaultH * targetScale } }) }) : m;
                });
            });
        };
        customEventHandler.addEventListener('multiSelectMoveStart', onStart);
        customEventHandler.addEventListener('multiSelectMove', onMove);
        customEventHandler.addEventListener('multiSelectMoveEnd', onEnd);
        return function () {
            customEventHandler.removeEventListener('multiSelectMoveStart', onStart);
            customEventHandler.removeEventListener('multiSelectMove', onMove);
            customEventHandler.removeEventListener('multiSelectMoveEnd', onEnd);
        };
        // positionRef keeps position current without putting it in deps (avoids mid-drag re-runs)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedTool, id, lock.positioning, frameId, canvasWidth, canvasHeight, setManipulative]);
    var handleClick = function (e) {
        var _a;
        if (panningEnable)
            return;
        e.stopPropagation();
        // iPad VoiceOver double-tap / keyboard Enter-Space synthesize a click
        // with `detail === 0`. For NF tokens, open the shared global context
        // menu so AT users can reach the counter actions. handleKeyDown covers
        // desktop keyboard Enter/Space, but iPad VO only fires click — not keydown.
        if (e.detail === 0 && isNfToken) {
            var rect = (_a = counterRef.current) === null || _a === void 0 ? void 0 : _a.getBoundingClientRect();
            if (!selectedToolRef.current.includes(id))
                setSelectedTool([id]);
            if (rect) {
                openCounterContextMenu(rect.right, rect.top);
            }
            return;
        }
        setSelectedTool(selectTool(e, id, selectedTool, false));
    };
    // Shape rendering based on type
    var renderShape = function () {
        var shapeStyle = {
            width: '100%',
            height: '100%',
            backgroundColor: color,
        };
        // Handle numberFrame tokens (shape0-shape7 pattern)
        if (typeof shape === 'string' && shape.startsWith('shape')) {
            // Extract the shape number (e.g., "shape0" -> "0")
            var shapeMatch = shape.match(/^shape(\d+)$/);
            if (shapeMatch) {
                // For numberFrame tokens, use the 1.svg from the asset folder
                var svgPath = "./assets/images/manipulative/numberFrames/".concat(color, "/").concat(shape, "/1.svg");
                return (_jsx("img", { src: svgPath, alt: "".concat(shape, " token"), style: { width: '100%', height: '100%', objectFit: 'contain' }, draggable: "false" }));
            }
        }
        switch (shape) {
            case 'circle':
                return (_jsx("div", { className: "counter-shape circle", style: __assign(__assign({}, shapeStyle), { borderRadius: '50%' }) }));
            case 'square':
                return _jsx("div", { className: "counter-shape square", style: shapeStyle });
            case 'star':
                // Placeholder - will be replaced with actual star SVG
                return (_jsx("div", { className: "counter-shape star", style: shapeStyle, children: _jsx("svg", { viewBox: "0 0 24 24", fill: color, children: _jsx("path", { d: "M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" }) }) }));
            case 'flower':
            case 'penguin':
            case 'frog':
            case 'butterfly':
            case 'ladybug':
                // Placeholder shapes - will be replaced with actual SVG assets
                return (_jsx("div", { className: "counter-shape placeholder", style: shapeStyle, children: _jsx("span", { style: { color: 'white', fontSize: '10px', textAlign: 'center' }, children: shape[0].toUpperCase() }) }));
            default:
                return (_jsx("div", { className: "counter-shape circle", style: __assign(__assign({}, shapeStyle), { borderRadius: '50%' }) }));
        }
    };
    var isSelected = selectedTool.includes(id);
    // Mirror of the spinner "focus on newly selected" pattern: when selectedTool
    // transitions from not-including this counter to including it (e.g. after
    // a Duplicate action moves selection to the new counter), move keyboard
    // focus onto the counter wrapper. Context-menu guard prevents stealing
    // focus from an open context menu.
    var prevSelectedToolRef = useRef([]);
    useEffect(function () {
        var wasSelected = prevSelectedToolRef.current.includes(id);
        var nowSelected = selectedTool.includes(id);
        prevSelectedToolRef.current = selectedTool;
        if (nowSelected && !wasSelected && isNfToken && counterRef.current) {
            setTimeout(function () {
                var _a, _b;
                if ((_a = document.activeElement) === null || _a === void 0 ? void 0 : _a.closest('.shape-context-menu'))
                    return;
                (_b = counterRef.current) === null || _b === void 0 ? void 0 : _b.focus();
            }, 100);
        }
    }, [selectedTool, id, isNfToken]);
    // Bottom panel event handling (scale, edit)
    useEffect(function () {
        if (!isSelected)
            return;
        var handleBottomPanel = function (event) {
            var _a, _b, _c, _d, _e, _f, _g, _h;
            if (!selectedTool.includes(id))
                return;
            switch (event.id) {
                case 'scaleMinus': {
                    var newScale_1 = Math.max(0.5, parseFloat((scale - 0.1).toFixed(1)));
                    if (newScale_1 !== scale) {
                        var defaultW_2 = (_b = (_a = data.defaultDimensions) === null || _a === void 0 ? void 0 : _a.width) !== null && _b !== void 0 ? _b : 50;
                        var defaultH_2 = (_d = (_c = data.defaultDimensions) === null || _c === void 0 ? void 0 : _c.height) !== null && _d !== void 0 ? _d : 50;
                        saveState({
                            type: id,
                            id: id,
                            propertyName: 'scale',
                            old: JSON.stringify(scale),
                            new: JSON.stringify(newScale_1),
                        });
                        setManipulative(function (prev) {
                            return prev.map(function (item) {
                                return item.data.id === id
                                    ? __assign(__assign({}, item), { data: __assign(__assign({}, item.data), { scale: newScale_1, dimensions: { width: defaultW_2 * newScale_1, height: defaultH_2 * newScale_1 } }) }) : item;
                            });
                        });
                        announce(t('text.manipulativeScaled', {
                            name: t('text.manipulativeCounter'),
                            direction: t('text.down'),
                            value: newScale_1.toFixed(1),
                        }));
                    }
                    break;
                }
                case 'scalePlus': {
                    var newScale_2 = parseFloat((scale + 0.1).toFixed(1));
                    var defaultW_3 = (_f = (_e = data.defaultDimensions) === null || _e === void 0 ? void 0 : _e.width) !== null && _f !== void 0 ? _f : 50;
                    var defaultH_3 = (_h = (_g = data.defaultDimensions) === null || _g === void 0 ? void 0 : _g.height) !== null && _h !== void 0 ? _h : 50;
                    saveState({
                        type: 'update',
                        id: id,
                        propertyName: 'scale',
                        old: JSON.stringify(scale),
                        new: JSON.stringify(newScale_2),
                    });
                    setManipulative(function (prev) {
                        return prev.map(function (item) {
                            return item.data.id === id
                                ? __assign(__assign({}, item), { data: __assign(__assign({}, item.data), { scale: newScale_2, dimensions: { width: defaultW_3 * newScale_2, height: defaultH_3 * newScale_2 } }) }) : item;
                        });
                    });
                    announce(t('text.manipulativeScaled', {
                        name: t('text.manipulativeCounter'),
                        direction: t('text.up'),
                        value: newScale_2.toFixed(1),
                    }));
                    break;
                }
                case 'edit': {
                    if (frameId) {
                        setManiplulatorComponentVisible(true);
                        setActiveManipulator('numberFrames');
                        setSelectedManipulativeId(frameId);
                    }
                    break;
                }
            }
        };
        customEventHandler.addEventListener('bottomPanel', handleBottomPanel);
        return function () { return customEventHandler.removeEventListener('bottomPanel', handleBottomPanel); };
    }, [isSelected, selectedTool, id, scale, data.defaultDimensions, frameId]);
    return (_jsxs(_Fragment, { children: [_jsx("div", __assign({ ref: counterRef, className: "counter ".concat(isDraggable ? 'draggable' : '', " ").concat(lock.positioning ? 'locked' : '', " ").concat(frameId ? 'in-frame' : '', " ").concat(isSelected ? 'selected' : '', " ").concat(isDragging || isMultiSelectMoving ? 'dragging' : '', " ").concat(isHovered ? 'hovered' : ''), 
                // Framed counters act as buttons so Enter / Space can open the shared
                // global context menu. Standalone counters keep role="img" (unchanged).
                role: isNfToken ? 'button' : 'img', tabIndex: isNfToken ? 0 : undefined, "aria-label": (function () {
                    // Use the human-readable shape name for NF tokens; fall back to the
                    // raw shape string for standalone Counter / Money pieces.
                    var shapeLabel = isNfToken && NF_SHAPE_LABEL_KEYS[shape]
                        ? t(NF_SHAPE_LABEL_KEYS[shape])
                        : shape;
                    var base = t('text.counterLabel', { shape: shapeLabel, color: color });
                    return cellPosition
                        ? "".concat(base, ", ").concat(t('text.cellPosition', { row: cellPosition.row, col: cellPosition.col }))
                        : base;
                })(), style: {
                    position: 'absolute',
                    left: actualPosition.x - width / 2,
                    top: actualPosition.y - height / 2,
                    width: width,
                    height: height,
                    transform: "rotate(".concat(rotation, "deg)"),
                    cursor: isDraggable && !panningEnable ? (isDragging ? 'grabbing' : 'grab') : 'default',
                    zIndex: isDragging || isFrameDragging ? 9999 : undefined,
                    pointerEvents: 'auto',
                }, "data-id": id, "data-manipulative-id": id }, (frameId ? { 'data-group-id': frameId } : {}), { onMouseDown: handleMouseDown, onMouseEnter: function () { return setIsHovered(true); }, onMouseLeave: function () { return setIsHovered(false); }, onTouchStart: handleTouchStart, onContextMenu: handleContextMenu, onClick: handleClick, onKeyDown: isNfToken ? handleKeyDown : undefined, children: renderShape() })), localContextMenu &&
                createPortal(_jsx("div", { ref: contextMenuRef, className: "counter-context-menu", style: { left: localContextMenu.x, top: localContextMenu.y }, onMouseDown: function (e) { return e.stopPropagation(); }, onTouchStart: function (e) { return e.stopPropagation(); }, children: _jsx("button", { className: "counter-context-item", onClick: toggleLock, onMouseEnter: function (e) { return e.currentTarget.classList.add('hovered'); }, onMouseLeave: function (e) { return e.currentTarget.classList.remove('hovered'); }, children: lock.positioning ? 'Unlock' : 'Lock' }) }), document.body)] }));
};
export default Counter;
